/**
 * PatientAdvanceReceiptsList — Receipts sub-tab of Patient Billing
 * (PROD-733).
 *
 * Flutter ref: `patient_advance_receipts_screen.dart`. Response is
 * envelope-wrapped (`PagedResults<AdvancePaymentReceipt>`) so we get
 * `totalNumberOfPages` for free — render a real page-N-of-M pager
 * instead of "load more".
 *
 * PR A: read-only. Refund + Void per-row actions defer to PR C.
 */

import { useState } from 'react';
import { StatusChip } from '@scripsteam/scrips-signal-ds';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import { usePatientAdvanceReceipts } from '@/patients/services/use-patient-advance-receipts';
import {
  BillingListFilterBar,
  EMPTY_BILLING_FILTER,
  type BillingFilterValue,
} from './billing-list-filter-bar';
import { BillingPager } from './billing-pager';
import { PatientReceiptRowActions } from './patient-receipt-row-actions';

import { formatMoney } from '@/shared/lib/money';
import { V2_BODY_EMPHASIS, V2_CARD_SURFACE, v2Look } from '@/shared/ui/design-v2';
export interface PatientAdvanceReceiptsListProps {
  patientId: string | null | undefined;
}

export function PatientAdvanceReceiptsList({
  patientId,
}: PatientAdvanceReceiptsListProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<BillingFilterValue>(
    EMPTY_BILLING_FILTER,
  );
  const onFilters = (next: BillingFilterValue) => {
    setPage(1);
    setFilters(next);
  };

  const { data, isFetching } = usePatientAdvanceReceipts({
    patientId,
    pageNumber: page,
    pageSize,
    query: filters.search || undefined,
    startDate: filters.startDate,
    endDate: filters.endDate,
  });

  const rows = data?.results ?? [];
  const totalPages = data?.totalNumberOfPages ?? 1;
  const totalRecords = data?.totalNumberOfRecords ?? 0;
  const hasActiveFilters = !!filters.search || !!filters.startDate;

  // Flutter receipts filter is search + date range only (no facets).
  const filterBar = (
    <BillingListFilterBar
      searchPlaceholder="Search by receipt #"
      value={filters}
      onChange={onFilters}
    />
  );

  if (isFetching && rows.length === 0) {
    return (
      <div className="space-y-3">
        {filterBar}
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="space-y-3">
        {filterBar}
        <div className="rounded-lg border border-dashed border-border-default p-8 text-center">
          <p className="text-sm text-text-muted">
            {hasActiveFilters
              ? 'No advance receipts match these filters.'
              : 'No advance receipts on file for this patient.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {filterBar}
      <div className={v2Look('rounded-lg border border-border-default overflow-hidden', V2_CARD_SURFACE)}>
        <table className="w-full text-sm">
          <thead className="bg-grey-100">
            <tr className="text-left text-text-muted">
              <Th>Receipt #</Th>
              <Th>Date</Th>
              <Th>Method</Th>
              <Th align="right">Amount</Th>
              <Th align="right">Available balance</Th>
              <Th>Status</Th>
              <Th>Linked invoice</Th>
              <Th align="right"> </Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={row.id ?? row.receiptNumber ?? `row-${i}`}
                className="border-t border-border-default hover:bg-grey-100/50"
              >
                <Td>
                  {/* PROD-2743 · switch ON: a record number is a value, not a link: body-emphasis in
                      text-primary, not brand ink. OFF = today. */}
                  <span className={v2Look('font-medium text-brand-depth', V2_BODY_EMPHASIS)}>
                    {row.receiptNumber ?? '—'}
                  </span>
                </Td>
                <Td>{formatDate(row.receiptDate)}</Td>
                <Td>{row.paymentMethod ?? '—'}</Td>
                <Td align="right">AED {formatMoney(row.receiptAmount ?? 0)}</Td>
                <Td align="right">
                  AED {formatMoney(row.availableBalance ?? 0)}
                </Td>
                <Td>
                  <ReceiptStatusCell
                    status={row.status ?? null}
                    voided={Boolean(row.voidedDate)}
                  />
                </Td>
                <Td>
                  {row.invoiceNumber ? (
                    <span className="text-text-secondary">
                      {row.invoiceNumber}
                    </span>
                  ) : (
                    <span className="text-text-hint">—</span>
                  )}
                </Td>
                <Td align="right">
                  <PatientReceiptRowActions
                    row={row}
                    patientId={patientId as string}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <BillingPager
        page={page}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        isFetching={isFetching}
        noun="receipts"
        onPage={setPage}
        onPageSize={(n) => {
          setPage(1);
          setPageSize(n);
        }}
      />
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={`px-3 py-2 text-xs font-bold uppercase tracking-wide ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <td
      className={`px-3 py-2 ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      {children}
    </td>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

function ReceiptStatusCell({
  status,
  voided,
}: {
  status: string | null;
  voided: boolean;
}) {
  if (voided) {
    return (
      <StatusChip semantic="error" tone="soft">
        Voided
      </StatusChip>
    );
  }
  if (!status) return <span className="text-text-hint">—</span>;
  return (
    <StatusChip semantic="info" tone="soft">
      {status}
    </StatusChip>
  );
}

